import React, { useEffect, useState, createContext, useContext } from 'react';
export interface Address {
  firstName: string;
  lastName: string;
  address: string;
  island: string;
  postalCode: string;
}
export interface OrderItem {
  productId: string;
  productName: string;
  productImage: string;
  quantity: number;
  unitPrice: number;
  selectedSize?: string;
  selectedColor?: string;
  selectedAddOns: string[];
}
export type OrderStatus = 'processing' | 'shipped' | 'delivered' | 'cancelled';
export interface Order {
  id: string;
  date: string;
  items: OrderItem[];
  subtotal: number;
  shipping: number;
  total: number;
  status: OrderStatus;
  shippingAddress: Address;
  email: string;
  phone: string;
}
export interface User {
  email: string;
  password: string; // mock only — never do this in production
  firstName: string;
  lastName: string;
  phone: string;
  addresses: Address[];
  orders: Order[];
}
interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
}
interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => {
    success: boolean;
    error?: string;
  };
  register: (data: RegisterData) => {
    success: boolean;
    error?: string;
  };
  logout: () => void;
  updateProfile: (data: Partial<User>) => void;
  addOrder: (order: Order) => void;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const USERS_KEY = 'beachShopUsers';
const CURRENT_KEY = 'beachShopCurrentUser';
function readUsers(): User[] {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    return raw ? JSON.parse(raw) as User[] : [];
  } catch {
    return [];
  }
}
function writeUsers(users: User[]) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const email = localStorage.getItem(CURRENT_KEY);
      if (!email) return null;
      const users = readUsers();
      return users.find(u => u.email === email) || null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (user) {
      localStorage.setItem(CURRENT_KEY, user.email);
      // Also keep the master users list in sync
      const users = readUsers();
      const idx = users.findIndex(u => u.email === user.email);
      if (idx >= 0) {
        users[idx] = user;
        writeUsers(users);
      }
    } else {
      localStorage.removeItem(CURRENT_KEY);
    }
  }, [user]);
  const login: AuthContextValue['login'] = (email, password) => {
    const users = readUsers();
    const found = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (!found) return {
      success: false,
      error: 'No account with that email.'
    };
    if (found.password !== password) return {
      success: false,
      error: 'Incorrect password.'
    };
    setUser(found);
    return {
      success: true
    };
  };
  const register: AuthContextValue['register'] = data => {
    const users = readUsers();
    if (users.some(u => u.email.toLowerCase() === data.email.toLowerCase())) {
      return {
        success: false,
        error: 'An account with that email already exists.'
      };
    }
    const newUser: User = {
      ...data,
      addresses: [],
      orders: []
    };
    users.push(newUser);
    writeUsers(users);
    setUser(newUser);
    return {
      success: true
    };
  };
  const logout = () => setUser(null);
  const updateProfile: AuthContextValue['updateProfile'] = data => {
    setUser(prev => prev ? {
      ...prev,
      ...data
    } : prev);
  };
  const addOrder: AuthContextValue['addOrder'] = order => {
    setUser(prev => prev ? {
      ...prev,
      orders: [order, ...prev.orders]
    } : prev);
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    addOrder
  }} data-id="element-228">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}